// =====================================================================
// PropertyCuts — export price preview
// =====================================================================
// The server charges with propertycut_render_price() (migration
// 20261004132000); renderPricing.test.ts keeps the two in step.
// =====================================================================

import {
  CREDITS_BUNDLE_TOTAL,
  CREDITS_SINGLE_FORMAT,
  BUNDLE_SPLIT,
} from '../constants.ts';
import type { RenderFormat } from '../types.ts';

// ---------------------------------------------------------------------
// Cost preview
// ---------------------------------------------------------------------

export interface RenderCostBreakdown {
  /** Total credits the user will be charged across all selected formats */
  total: number;
  /** Per-format charge (sums to `total`) — matches the rows we'll insert */
  perFormat: Array<{ format: RenderFormat; credits: number }>;
}

/**
 * Compute the credit breakdown for a multi-format render. Pure — does
 * not touch the network. Use this to render a cost preview in the UI.
 *
 * Pricing rules:
 *   - Single format: CREDITS_SINGLE_FORMAT
 *   - All three formats: CREDITS_BUNDLE_TOTAL (split per BUNDLE_SPLIT)
 *   - Partial selection (e.g. 2 formats): summed from the bundle weights
 *     in `perFormat`.
 */
export function computeRenderCost(formats: RenderFormat[]): RenderCostBreakdown {
  if (formats.length === 0) {
    return { total: 0, perFormat: [] };
  }

  if (formats.length === 1) {
    return {
      total: CREDITS_SINGLE_FORMAT,
      perFormat: [{ format: formats[0], credits: CREDITS_SINGLE_FORMAT }],
    };
  }

  // Multi-format: charge per-format weights from BUNDLE_SPLIT. The weights
  // only add up to CREDITS_BUNDLE_TOTAL when all three formats are
  // selected, which is the documented bundle case.
  const perFormat = formats.map((f) => ({ format: f, credits: BUNDLE_SPLIT[f] }));
  const total =
    formats.length === 3
      ? CREDITS_BUNDLE_TOTAL
      : perFormat.reduce((sum, p) => sum + p.credits, 0);

  return { total, perFormat };
}
